package ui.screens

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import ui.components.Navbar
import ui.components.Footer as SiteFooter
import org.w3c.dom.HTMLElement
import kotlinx.browser.window

@Composable
fun PrivacyScreen(
    onBack: () -> Unit,
    onNavigateToTerms: () -> Unit = {}
) {
    var showBackToTop by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        window.onscroll = {
            showBackToTop = window.pageYOffset > 400
            null
        }
    }

    Div(attrs = {
        classes("min-h-screen", "bg-white", "dark:bg-medical-bgDark", "transition-colors", "duration-300")
    }) {
        Navbar(onNavigateHome = onBack)

        Div(attrs = {
            classes("max-w-4xl", "mx-auto", "pt-40", "pb-20", "px-6")
        }) {
            // Header
            Header {
                H1(attrs = { classes("text-3xl", "md:text-4xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-4") }) {
                    Text("Política de Privacidad")
                }
                P(attrs = { classes("text-gray-500", "dark:text-gray-400", "mb-12") }) {
                    Text("Última actualización: 24 de Julio de 2026")
                }
            }

            Section(attrs = { classes("prose", "prose-slate", "dark:prose-invert", "max-w-none", "text-gray-700", "dark:text-gray-300", "leading-relaxed") }) {
                
                P(attrs = { classes("mb-8") }) {
                    Text("Esta Política de Privacidad describe cómo la aplicación DrRecipe (en adelante, “la Aplicación”) recopila, utiliza y protege la información personal de sus usuarios, destinada exclusivamente a profesionales de la salud. Al utilizar la Aplicación, usted acepta las prácticas aquí descritas.")
                }

                LegalSection("1. Responsable del Tratamiento de Datos") {
                    P { Text("El responsable del tratamiento de los datos es el desarrollador de DrRecipe. Para cualquier consulta relacionada con privacidad y protección de datos, puede contactarnos en:") }
                    P {
                        Text("📧 ")
                        A(href = "mailto:soporte@drrecipeapp.com", attrs = { classes("text-medical-primary", "font-bold", "no-underline") }) {
                            Text("soporte@drrecipeapp.com")
                        }
                    }
                }

                LegalSection("2. Información que Recopilamos") {
                    P { Text("Durante el uso de la Aplicación, podemos recopilar la siguiente información:") }
                    
                    P(attrs = { classes("font-bold", "mt-4") }) { Text("Datos del profesional de la salud") }
                    Ul {
                        ListItem { Text("Nombre y apellido") }
                        ListItem { Text("País de ejercicio profesional") }
                        ListItem { Text("Número de matrícula profesional") }
                        ListItem { Text("Colegio, consejo o entidad profesional") }
                        ListItem { Text("Dirección de correo electrónico (gestionada mediante Firebase Authentication)") }
                        ListItem { Text("Imagen del perfil") }
                        ListItem { Text("Especialidad") }
                    }

                    P(attrs = { classes("font-bold", "mt-4") }) { Text("Datos biométricos y gráficos") }
                    Ul {
                        ListItem { Text("Firma digital dibujada por el usuario") }
                        ListItem { Text("Imagen del sello profesional") }
                    }

                    P(attrs = { classes("font-bold", "mt-4") }) { Text("Información técnica") }
                    Ul {
                        ListItem { Text("Datos técnicos básicos del dispositivo (utilizados únicamente con fines de seguridad, estabilidad y mejora del servicio)") }
                        ListItem { Text("Identificadores de dispositivo y tokens de notificación (Firebase Cloud Messaging) para el envío de alertas sobre el estado de su verificación y recordatorios de suscripción.") }
                    }

                    P(attrs = { classes("font-bold", "mt-4") }) { Text("Datos de pacientes") }
                    P { Text("Información clínica ingresada por el profesional bajo el consentimiento libre, expreso e informado del titular de los datos (el paciente), gestionado por el médico tratante a través de las herramientas de validación de la Aplicación.") }
                }

                LegalSection("3. Finalidad del Tratamiento") {
                    P { Text("La información recopilada se utiliza exclusivamente para:") }
                    Ul {
                        ListItem { Text("Verificar la identidad del profesional de la salud") }
                        ListItem { Text("Habilitar las funcionalidades de la Aplicación") }
                        ListItem { Text("Generar documentos médicos en formato digital (PDF)") }
                        ListItem { Text("Almacenar firma y sello profesional para su uso en documentos") }
                        ListItem { Text("Mejorar la seguridad, funcionamiento y experiencia del usuario") }
                        ListItem { Text("Cumplir con requerimientos legales cuando sea necesario") }
                    }
                    P { Text("El Usuario (profesional de la salud) es el único responsable de garantizar que cuenta con la base legal necesaria (como el consentimiento informado) para el tratamiento de los datos de salud de sus pacientes.") }
                    P { Text("DrRecipe no utiliza los datos con fines publicitarios ni comerciales.") }
                }

                LegalSection("4. Almacenamiento y Seguridad") {
                    P { Text("Los datos se almacenan utilizando servicios seguros de terceros, como Firebase (Google), que aplican estándares reconocidos de seguridad y cifrado.") }
                    P { Text("Se recomienda al Usuario activar las funciones de seguridad biométrica (huella dactilar o reconocimiento facial) de su dispositivo para proteger el acceso a la Aplicación y a los datos de los pacientes almacenados localmente.") }
                    P { Text("La Aplicación utiliza una base de datos local (Room) para permitir el acceso offline a los registros clínicos. Estos datos permanecen en el dispositivo hasta que el usuario cierre sesión o elimine la aplicación.") }
                    Ul {
                        ListItem { Text("El acceso a los datos está limitado a los fines descritos en esta política") }
                        ListItem { Text("Se aplican medidas razonables para proteger la información contra accesos no autorizados") }
                    }
                    P { Text("No obstante, ningún sistema es completamente infalible, por lo que DrRecipe no puede garantizar seguridad absoluta.") }
                }

                LegalSection("5. Compartición de Información") {
                    P { Text("DrRecipe no vende ni comercializa datos personales.") }
                    P { Text("La información solo podrá ser compartida en los siguientes casos:") }
                    Ul {
                        ListItem { Text("Cumplimiento de obligaciones legales o requerimientos judiciales") }
                        ListItem { Text("Prevención de fraudes o actividades ilegales") }
                        ListItem { Text("Consentimiento expreso del usuario") }
                    }
                    P { Text("La documentación profesional (licencia, firma y sello) será accesible para nuestro personal administrativo con el único fin de validar su identidad. Una vez aprobada o rechazada la solicitud, el acceso a estos documentos se restringe según nuestras políticas de seguridad interna.") }
                }

                LegalSection("6. Conservación de los Datos") {
                    P { Text("Los datos personales se conservarán mientras la cuenta del usuario permanezca activa.") }
                    P { Text("El usuario puede solicitar la eliminación total de su cuenta desde la propia Aplicación. Al confirmar esta acción, DrRecipe procederá a:") }
                    Ul {
                        ListItem { Text("Eliminar los datos del perfil del usuario") }
                        ListItem { Text("Eliminar firma digital y sello profesional almacenados") }
                        ListItem { Text("Eliminar credenciales enviadas para validación") }
                        ListItem { Text("Eliminar los datos de autenticación asociados a la cuenta") }
                        ListItem { Text("Eliminar los archivos y registros vinculados al usuario, incluyendo documentos almacenados") }
                    }
                    P { B { Text("Importante:") }; Text(" La eliminación de la cuenta es permanente e irreversible.") }
                    P { Text("Podrán conservarse determinados datos únicamente cuando exista una obligación legal o requerimiento judicial que lo justifique.") }
                }

                LegalSection("7. Derechos del Usuario") {
                    P { Text("El usuario tiene derecho a:") }
                    Ul {
                        ListItem { Text("Acceder a sus datos personales") }
                        ListItem { Text("Rectificar información incorrecta") }
                        ListItem { Text("Solicitar la eliminación de sus datos") }
                        ListItem { Text("Revocar el consentimiento otorgado") }
                    }
                    P {
                        Text("Para ejercer estos derechos, puede contactarnos en: ")
                        A(href = "mailto:soporte@drrecipeapp.com", attrs = { classes("text-medical-primary", "font-bold", "no-underline") }) {
                            Text("soporte@drrecipeapp.com")
                        }
                    }
                }

                LegalSection("8. Menores de Edad") {
                    P { Text("DrRecipe está destinada exclusivamente a profesionales de la salud. La Aplicación no está dirigida a menores de 18 años y no recopila intencionalmente información de menores.") }
                }

                LegalSection("9. Cambios en la Política") {
                    P { Text("DrRecipe se reserva el derecho de modificar esta Política de Privacidad en cualquier momento. Las modificaciones serán notificadas dentro de la Aplicación y entrarán en vigencia desde su publicación.") }
                    P { Text("El uso continuado de la Aplicación implica la aceptación de los cambios realizados.") }
                }

                LegalSection("10. Transferencias Internacionales de Datos") {
                    P { Text("Al utilizar servicios de infraestructura en la nube (como Firebase de Google), parte de su información y la de sus pacientes puede ser procesada o almacenada en servidores ubicados fuera de su país de origen. DrRecipe asegura que dichos proveedores cumplen con estándares internacionales de protección y seguridad de datos.") }
                }

                LegalSection("11. Legislación Aplicable") {
                    P { Text("Esta Política de Privacidad se rige por las leyes vigentes de la República Bolivariana de Venezuela, sin perjuicio de los derechos que correspondan al usuario según la legislación de su país de residencia en materia de protección de datos personales.") }
                }

                // --- NUEVO: NAVEGACIÓN ENTRE PÁGINAS LEGALES ---
                Div(attrs = { classes("mt-20", "pt-10", "border-t", "border-gray-200", "dark:border-medical-surfaceVariantDark", "flex", "justify-between", "items-center") }) {
                    Div {
                        P(attrs = { classes("text-sm", "text-gray-500", "dark:text-gray-400", "mb-1") }) { Text("¿Deseas leer nuestras reglas de uso?") }
                        A(href = "javascript:void(0)", attrs = { 
                            classes("text-medical-primary", "dark:text-medical-primaryDark", "font-bold", "hover:underline") 
                            onClick { window.location.href = "/?p=terms" }
                        }) { Text("Ver Términos y Condiciones →") }
                    }
                }
            }

        }

        SiteFooter(
            onNavigateToTerms = onNavigateToTerms,
            onNavigateToPrivacy = {}
        )

        if (showBackToTop) {
            Div(attrs = {
                classes("fixed", "bottom-8", "right-8", "z-50", "bg-medical-primary", "dark:bg-medical-primaryDark", "text-white", "dark:text-medical-bgDark", "p-4", "rounded-full", "shadow-2xl", "cursor-pointer")
                onClick { window.scrollTo(0.0, 0.0) }
            }) { Span(attrs = { classes("material-symbols-outlined") }) { Text("keyboard_arrow_up") } }
        }
    }
}

@Composable
private fun LegalSection(title: String, content: ContentBuilder<HTMLElement>) {
    Div(attrs = { classes("mb-10") }) {
        H2(attrs = { classes("text-xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-4") }) { Text(title) }
        content()
    }
}

@Composable
private fun ListItem(content: ContentBuilder<HTMLElement>) {
    Li(attrs = { classes("mb-2", "ml-4", "list-disc") }) { content() }
}
